firebird.factory('appInsightsService', [
    '$window',
    '$rootScope',
    'helperService',
    'logService',
    'configService',
    function ($window, $rootScope, helperService, logService, configService) {
        const logger = logService.createLoggerWithSource('appInsightsService');
        var appInsights;
        var metricThresholds;

        // Event queue for storing events before App Insights is loaded
        var eventQueue = [];

        const config = {
            initialized: false,
            loaded: false,
            hasLoadBeenCalled: false,
            appInsightsConfig: undefined,
            enabled: true, // Default to true
            sessionId: undefined,
            scriptSrc: 'lib/applicationinsights/2.3.0/applicationinsights-web.min.js'
        };

        var service = {
            initialize: initialize,
            loadExternal: loadExternal
        };

        $rootScope.$on('metrics-video-room', processMetrics);

        return service;

        function initialize(context) {
            // Store session ID for when we load App Insights
            config.sessionId = context && context.sessionId;

            // Get AppInsights config once and store it for later use
            var appInsightsConfig = helperService.getAppInsightsConfig();
            if (!appInsightsConfig || !appInsightsConfig.instrumentationKey) {
                logger.error('initialize', 'AppInsights configuration is missing or invalid');
                return;
            }
            // Save the entire config for later use in loadExternal
            config.appInsightsConfig = appInsightsConfig;
            // Set metric thresholds for immediate use
            metricThresholds = appInsightsConfig.metricThresholds || {};

            setPerformanceMetricsListeners();
            config.initialized = true;
            if (config.hasLoadBeenCalled && !config.loaded) {
                loadExternal();
            }
        }

        function loadExternal() {
            if (!config.initialized) {
                config.hasLoadBeenCalled = !config.loaded;
                logger.log('loadExternal', 'AppInsights not initialized');
                return;
            }

            if (config.loaded) {
                logger.log('loadExternal', 'AppInsights already loaded externally');
                return;
            }

            logger.log('loadExternal', 'Loading AppInsights externally');
            config.loaded = true;

            // Check if AppInsights is disabled in config
            config.enabled = configService.getServerValue('enableAppInsights') !== 'false';
            if (!config.enabled) {
                logger.log('loadExternal', 'AppInsights disabled via configuration');
                eventQueue = []; // Clear the event queue if AppInsights is disabled
                return;
            }

            // Add Application Insights script dynamically
            var scriptId = 'app-insights-script';
            if (!document.getElementById(scriptId)) {
                var script = document.createElement('script');
                script.id = scriptId; // this is used to avoid double include of script.
                script.setAttribute('type', 'text/javascript');
                script.addEventListener('load', function (event) {
                    logger.log('loadExternal', `Loaded Application Insights script - ${scriptId}`);
                    setupAppInsights();
                });
                script.setAttribute('src', config.scriptSrc);
                document.head.appendChild(script);
            } else {
                // Script already exists, proceed with initialization
                setupAppInsights();
            }
        }

        function setupAppInsights() {
            const appInsightsConfig = config.appInsightsConfig;
            appInsights = new Microsoft.ApplicationInsights.ApplicationInsights({
                config: {
                    instrumentationKey: appInsightsConfig.instrumentationKey
                }
            });

            appInsights.loadAppInsights(false);

            if (config.sessionId) {
                appInsights.context.session.id = config.sessionId;
            }

            // Process any queued events
            processEventQueue();
        }

        function processEventQueue() {
            logger.log('processEventQueue', `Processing ${eventQueue.length} queued events`);

            // Process each queued event by calling processMetrics with the saved event data
            while (eventQueue.length > 0) {
                const eventData = eventQueue.shift();
                try {
                    processMetrics(null, eventData);
                } catch (error) {
                    logger.warn('processEventQueue', 'Error processing queued event:', error);
                }
            }
        }

        function setPerformanceMetricsListeners() {
            $window.preloadedApi.remote.ipcRenderer.onCpuMetricsReceive(function (_, data) {
                broadVideoroomRequestEvent(data);
            });

            $window.preloadedApi.remote.ipcRenderer.onProcessMetricsReceive(function (_, data) {
                var transformed = transformMetricsData(data);
                $rootScope.$broadcast('received-cpu-metrics-video-room', transformed);

                // receiver does detection for thresholds.
                if (thresholdExceeded(transformed)) {
                    broadVideoroomRequestEvent(transformed);
                }
            });
        }

        function broadVideoroomRequestEvent(data) {
            $rootScope.$broadcast('request-metrics-video-room', { metrics: data });
        }

        function processMetrics(_, data) {
            if (!data || !data.metrics) {
                return;
            }

            if (!config.initialized || !config.enabled) {
                return;
            }

            // If AppInsights is not loaded yet, queue the event data for later processing
            if (!config.loaded && appInsights === undefined) {
                eventQueue.push(data);
                logger.debug('processMetrics', 'Queued metrics event for later processing');
                return;
            }

            // Process the metrics now that AppInsights is available
            if (typeof data.metrics === 'number') {
                // CPU load only
                sendCpuUsageEvent(data.metrics, data.videoroomData);
            } else {
                // CPU, GPU metrics
                sendProcessMetricsEvent(data.metrics, data.videoroomData, data.gpuName, data.email);
            }
        }

        function sendCpuUsageEvent(cpuValue, videoroomData) {
            var cpuType = getCpuType();

            var commonProperties = {
                load: cpuValue,
                cpuType: cpuType
            };

            appInsights.trackEvent({
                name: 'cpu-usage',
                properties: videoroomData ? Object.assign(commonProperties, videoroomData) : commonProperties
            });
        }

        function sendProcessMetricsEvent(metrics, videoroomData, gpuName, email) {
            var result = {};

            if (videoroomData) {
                result.videoroomData = Object.assign(videoroomData);
            }

            if (metrics.cpu) {
                result.cpu = {
                    load: metrics.cpu,
                    loadTotal: metrics.cpuLoadTotal,
                    type: metrics.cpuModel ? metrics.cpuModel : getCpuType()
                };
            }

            if (metrics.memory) {
                result.memory = metrics.memory;
                result.memoryTotal = metrics.memoryTotal;
            }

            if (metrics.gpu) {
                result.gpu = {
                    load: metrics.gpu,
                    memory: metrics.gpuMemory,
                    name: gpuName
                };
            }

            if (appInsights.context.user && !appInsights.context.user.accountId) {
                appInsights.context.user.accountId = email;
            }

            appInsights.trackEvent({
                name: 'process-metrics',
                properties: result
            });
        }

        function getCpuType() {
            return helperService.getSystemInfo().cpuModel;
        }

        function transformMetricsData(data) {
            var meetingWindowMetrics = data['[MeetingWindow]'];
            var gpuProcessMetrics = data['[GPU Helper]'];

            var cpu = meetingWindowMetrics && meetingWindowMetrics.cpu && meetingWindowMetrics.cpu.percentCPUUsage;
            if (typeof cpu === 'number') {
                cpu = Math.round(cpu * 10) / 10; // up to one decimal
            }

            var gpu = gpuProcessMetrics && gpuProcessMetrics.cpu && gpuProcessMetrics.cpu.percentCPUUsage;
            if (typeof gpu === 'number') {
                gpu = Math.round(gpu * 10) / 10; // up to one decimal
            }

            return {
                cpu: cpu,
                cpuLoadTotal: meetingWindowMetrics?.cpuLoadTotal,
                cpuModel: meetingWindowMetrics?.cpuModel,
                memory: meetingWindowMetrics?.memory?.workingSetSize,
                memoryTotal: meetingWindowMetrics?.memoryTotal,
                gpu: gpu,
                gpuMemory: gpuProcessMetrics?.memory?.workingSetSize
            };
        }

        function thresholdExceeded(data) {
            if (!data) {
                return false;
            }

            return (
                Number(data.cpu) >= metricThresholds.cpu ||
                Number(data.memory) >= metricThresholds.memory ||
                Number(data.gpu) >= metricThresholds.gpu ||
                Number(data.gpuMemory) >= metricThresholds.gpuMemory
            );
        }
    }
]);
